<script setup lang="ts">
import { onMounted, onUpdated, ref } from "vue";
import { useRoute } from "vue-router";
import { Trip } from "@/models/Trip";
import TripOverview from "@/components/TripOverview/TripOverview.vue";
import TripInvoices from "@/components/TripInvoices/TripInvoices.vue";
import TripBudgetOverview from "@/components/TripBudgetOverview/TripBudgetOverview.vue";
import TripCrew from "@/components/TripCrew/TripCrew.vue";
import InviteCrewMemberModal from "@/components/InviteCrewMemberModal/InviteCrewMemberModal.vue";
import ManuallyAddCrewMemberModal from "@/components/ManuallyAddCrewMemberModal/ManuallyAddCrewMemberModal.vue";
import TripItenary from "@/components/TripItenary/TripItenary.vue";
import TripSavedOffers from "@/components/TripSavedOffers/TripSavedOffers.vue";
import { useI18n } from 'vue-i18n';
import {useTripsStore} from "@/stores/tripsStore"; // Import i18n


const { t } = useI18n(); // Initialize i18n
const route = useRoute();
const tripStore = useTripsStore()
const trip = ref<Trip>({});
const loading = ref(false);
const showInviteCrewMemberModal = ref(false);
const manuallyAddCrewMemberModal = ref(false);
const tab = ref('overview');

const updateTrip = async () => {
  try {
    const updatedTrip = await tripStore.get(trip.value.uuid)
    trip.value = updatedTrip.data;
  } catch(err) {
    console.log('Request failed: ', err.message);
  }
}

onMounted(() => {
  loading.value = true;
  tripStore.get(route.params.id)
      .then((data) => {
        if(!data || !data.data){
          return route.push({path: 'notfound'})
        }
        trip.value = data.data
        interval = setInterval(updateTrip, 5000);
      }).catch(() => {
          route.push({path: 'notfound'})
      }).finally(() => loading.value = false)
});
// Add class for the container inside the header
onUpdated(() => {
  const el = document.querySelector('.el-tabs__nav-scroll');
  el.classList.add('container');
});


</script>

<template>
  <div class="bg-primary-mouve" v-if="trip !== null">
    <div class="pt-8">
      <div class="container top flex justify-between">
        <div class="flex items-center gap-4">
          <img src="@/assets/icons/svg/Left_16x16.svg" alt="Back" width="16" class="cursor-pointer"
            @click="$router.back()">
          <img :src="trip.backgroundImageUrl" alt="trip"
            class="w-[7vw] h-17 object-cover rounded-11xl">
          <div class="responsive-title-3 font-semibold">{{ trip?.name }}</div>
        </div>
        <div class="border-black border border-solid w-12 h-7 flex justify-center items-center rounded-11xl">
          <img src="@/assets/icons/svg/Menu dots_16x16.svg" alt="Menu">
        </div>
      </div>
      <div class="bottom mt-8">
        <div class="absolute z-10 right-12 desktop" v-if="tab === 'crew'">
          <v-menu>
            <template v-slot:activator="{ props }">
              <button class="transformY bg-black text-white rounded-11xl px-5 py-1" v-bind="props">{{
    t('addCrewMembers') }}</button>
            </template>
            <v-list class="mt-5 w-52">
              <v-list-item class="cursor-pointer" @click="showInviteCrewMemberModal = true">
                <v-list-item-title>{{ t('inviteCrewMember') }}</v-list-item-title>
              </v-list-item>
              <v-list-item class="cursor-pointer" @click="manuallyAddCrewMemberModal = true">
                <v-list-item-title>{{ t('addManually') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <ElTabs class="bg-primary-mouve" :model-value="tab" @tab-change="(newTab) => { tab = newTab }" v-if="trip !== null">
          <div class="mt-6">
            <ElTabPane :label="t('overview')" name="overview" :lazy="true">
              <TripOverview :trip="trip" v-if="trip" @go-to-offers="() => { tab = 'offers' }"
                @go-to-crew="() => tab = 'crew'" @go-to-itinerary="() => tab = 'itinerary'"
                @go-to-budget="() => tab = 'budget'" />
            </ElTabPane>
            <ElTabPane :label="t('savedOffers')" name="offers" :lazy="true">
              <TripSavedOffers :trip="trip" v-if="trip" />
            </ElTabPane>
            <ElTabPane :label="t('itinerary')" name="itinerary" :lazy="true">
              <TripItenary :id="trip.uuid" />
            </ElTabPane>
            <ElTabPane :label="t('crewMembers')" name="crew">
              <TripCrew :tripId="trip.uuid" />
            </ElTabPane>
            <ElTabPane :label="t('invoices')" name="invoices">
              <TripInvoices :tripId='trip.uuid' />
            </ElTabPane>
            <ElTabPane :label="t('tripBudget')" name="budget">
              <TripBudgetOverview :id="trip.uuid" :lazy="true" />
            </ElTabPane>
          </div>
        </ElTabs>
      </div>
    </div>
  </div>
  <InviteCrewMemberModal @on-close="showInviteCrewMemberModal = false" :open="showInviteCrewMemberModal" />
  <ManuallyAddCrewMemberModal :open="manuallyAddCrewMemberModal" @on-close="manuallyAddCrewMemberModal = false" />
</template>

<style scoped>
.transformY {
  transform: translateY(-10px);
}

.border-black {
  border-color: black !important;
}

:deep(.el-tabs__content) {
  @apply bg-white;
}

:deep(.el-tabs__item:hover, .el-tabs__item.is-active) {
  color: black;
  border: black;
}

:deep(.el-tabs__item.is-active) {
  color: black;
  font-weight: 600;
}

:deep(.el-collapse-item__arrow) {
  transform: rotate(0deg);
}

:deep(.el-collapse-item__arrow.is-active) {
  transform: rotate(-90deg);
}

:deep(.el-tabs__header) {
  margin: 0;
}

:deep(.el-tabs__active-bar) {
  background-color: black;
  height: 3px;
}

@media (max-width: 768px) {
  .desktop {
    display: none;
  }
}
</style>
